import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "../../lib/api";
import { Calendar, RefreshCw, TrendingUp, ShoppingBag, Eye, MousePointerClick } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;

function dateRangePresets() {
  const today = new Date();
  const fmt = (d) => d.toISOString().slice(0, 10);
  const back = (days) => {
    const d = new Date(today);
    d.setDate(d.getDate() - days);
    return d;
  };
  return [
    { id: "today", label: "Today", from: fmt(today), to: fmt(today) },
    { id: "7d", label: "Last 7 days", from: fmt(back(6)), to: fmt(today) },
    { id: "30d", label: "Last 30 days", from: fmt(back(29)), to: fmt(today) },
    { id: "90d", label: "Last 90 days", from: fmt(back(89)), to: fmt(today) },
    { id: "ytd", label: "Year to date", from: `${today.getFullYear()}-01-01`, to: fmt(today) },
  ];
}

export default function AdminReports() {
  const presets = useMemo(dateRangePresets, []);
  const [range, setRange] = useState({ from: presets[1].from, to: presets[1].to });
  const [tab, setTab] = useState("sales");
  const [sales, setSales] = useState(null);
  const [meta, setMeta] = useState(null);
  const [utm, setUtm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ utm_source: "", utm_campaign: "" });

  const fetchAll = async () => {
    setLoading(true);
    const params = { date_from: range.from, date_to: range.to };
    try {
      const [s, m, u] = await Promise.all([
        api.get("/admin/reports/sales", { params: { ...params, ...filters } }),
        api.get("/admin/reports/meta", { params }),
        api.get("/admin/reports/utm", { params }),
      ]);
      setSales(s.data);
      setMeta(m.data);
      setUtm(u.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [range.from, range.to]);

  return (
    <div className="p-6 md:p-10" data-testid="admin-reports">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 gap-4">
        <div>
          <span className="font-script text-3xl text-blush-400">numbers, gently</span>
          <h1 className="font-heading text-3xl md:text-4xl text-ink-900">Reports & analytics</h1>
        </div>
        <button
          onClick={fetchAll}
          disabled={loading}
          className="btn-ghost-rose inline-flex items-center gap-2 text-xs"
          data-testid="reports-refresh"
        >
          <RefreshCw size={12} strokeWidth={1.6} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </header>

      {/* Filters */}
      <div className="rounded-3xl bg-white border border-blush-100 p-5 mb-8 flex flex-wrap items-end gap-4">
        <div className="flex flex-wrap items-center gap-2">
          {presets.map((p) => (
            <button
              key={p.id}
              onClick={() => setRange({ from: p.from, to: p.to })}
              className={`px-4 py-1.5 rounded-full text-xs font-body uppercase tracking-[0.18em] transition ${
                range.from === p.from && range.to === p.to
                  ? "bg-blush-600 text-white"
                  : "bg-blush-50 text-ink-700 hover:bg-blush-100"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Calendar size={14} strokeWidth={1.6} className="text-ink-500" />
          <input type="date" className="field !py-2 !px-3 text-sm" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} data-testid="reports-from" />
          <span className="text-ink-500">→</span>
          <input type="date" className="field !py-2 !px-3 text-sm" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} data-testid="reports-to" />
        </div>
        {tab === "sales" && (
          <div className="flex items-center gap-2 ml-auto">
            <input
              placeholder="utm_source"
              className="field !py-2 !px-3 text-sm w-36"
              value={filters.utm_source}
              onChange={(e) => setFilters((f) => ({ ...f, utm_source: e.target.value }))}
            />
            <input
              placeholder="utm_campaign"
              className="field !py-2 !px-3 text-sm w-44"
              value={filters.utm_campaign}
              onChange={(e) => setFilters((f) => ({ ...f, utm_campaign: e.target.value }))}
            />
            <button onClick={fetchAll} className="btn-primary text-xs">Apply</button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-blush-100">
        {[
          { id: "sales", label: "Sales" },
          { id: "meta", label: "Meta ROAS" },
          { id: "utm", label: "UTM tracking" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`reports-tab-${t.id}`}
            className={`px-5 py-3 font-body text-sm uppercase tracking-[0.18em] border-b-2 transition ${
              tab === t.id ? "border-blush-600 text-blush-600" : "border-transparent text-ink-700 hover:text-blush-600"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "sales" && sales && <SalesView sales={sales} />}
      {tab === "meta" && meta && <MetaView meta={meta} />}
      {tab === "utm" && utm && <UTMView utm={utm} />}
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="rounded-2xl bg-white border border-blush-100 p-5">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-body uppercase tracking-[0.2em] text-ink-500">{label}</p>
        {Icon && <Icon size={14} strokeWidth={1.6} className="text-blush-600" />}
      </div>
      <p className="mt-3 font-heading text-2xl text-ink-900">{value}</p>
    </div>
  );
}

function SalesView({ sales }) {
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Stat label="Revenue" value={fmt(sales.totals.revenue)} icon={TrendingUp} />
        <Stat label="Orders" value={sales.totals.orders} icon={ShoppingBag} />
        <Stat label="Avg order value" value={fmt(sales.totals.avg_order_value)} />
      </div>

      <Table title="By traffic source" headers={["Source", "Orders", "Revenue"]} rows={sales.by_source.map((r) => [r.source, r.orders, fmt(r.revenue)])} />
      <Table title="By campaign" headers={["Campaign", "Source", "Orders", "Revenue"]} rows={sales.by_campaign.map((r) => [r.campaign, r.source, r.orders, fmt(r.revenue)])} />
      <Table title="Top products" headers={["Product", "Qty sold", "Revenue"]} rows={sales.by_product.map((r) => [r.name, r.qty, fmt(r.revenue)])} />
    </div>
  );
}

function MetaView({ meta }) {
  if (!meta.ok) {
    return (
      <div className="rounded-3xl bg-white border border-blush-100 p-10 text-center">
        <p className="font-heading text-2xl text-ink-900">Meta Ads not connected</p>
        <p className="mt-2 font-body text-ink-500 text-sm">{meta.error}</p>
        <p className="mt-4 font-body text-ink-700 text-sm">
          Add your Pixel ID, Ad Account, and Marketing API access token in <span className="text-blush-600">Settings → Meta</span>.
        </p>
      </div>
    );
  }
  const t = meta.totals;
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <Stat label="Spend" value={fmt(t.spend)} />
        <Stat label="Impressions" value={(t.impressions || 0).toLocaleString()} icon={Eye} />
        <Stat label="Clicks" value={(t.clicks || 0).toLocaleString()} icon={MousePointerClick} />
        <Stat label="Revenue (matched)" value={fmt(t.revenue)} />
        <Stat label="Orders" value={t.orders} icon={ShoppingBag} />
        <Stat label="Overall ROAS" value={`${t.roas}×`} icon={TrendingUp} />
      </div>
      <div className="rounded-3xl bg-white border border-blush-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-blush-100">
          <h3 className="font-heading text-xl text-ink-900">Per-campaign performance</h3>
          <p className="text-xs font-body text-ink-500 mt-1">
            Revenue is matched by <code className="text-blush-600">utm_campaign = Meta campaign name</code>.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-body">
            <thead>
              <tr className="bg-blush-50/40 text-left text-[11px] uppercase tracking-[0.18em] text-ink-500">
                <th className="px-6 py-3">Campaign</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Spend</th>
                <th className="px-6 py-3">Impr</th>
                <th className="px-6 py-3">Clicks</th>
                <th className="px-6 py-3">CTR</th>
                <th className="px-6 py-3">CPC</th>
                <th className="px-6 py-3">Orders</th>
                <th className="px-6 py-3">Revenue</th>
                <th className="px-6 py-3">ROAS</th>
              </tr>
            </thead>
            <tbody>
              {meta.campaigns.length === 0 ? (
                <tr><td colSpan={10} className="px-6 py-10 text-center text-ink-500">No campaigns in this date range.</td></tr>
              ) : meta.campaigns.map((c) => (
                <tr key={c.campaign_id} className="border-t border-blush-100">
                  <td className="px-6 py-3 text-ink-900">{c.campaign_name}</td>
                  <td className="px-6 py-3 text-xs">{c.status || "—"}</td>
                  <td className="px-6 py-3">{fmt(c.spend)}</td>
                  <td className="px-6 py-3">{(c.impressions || 0).toLocaleString()}</td>
                  <td className="px-6 py-3">{(c.clicks || 0).toLocaleString()}</td>
                  <td className="px-6 py-3">{c.ctr}%</td>
                  <td className="px-6 py-3">{fmt(c.cpc)}</td>
                  <td className="px-6 py-3">{c.orders}</td>
                  <td className="px-6 py-3">{fmt(c.revenue)}</td>
                  <td className={`px-6 py-3 font-medium ${c.roas >= 2 ? "text-emerald-700" : c.roas >= 1 ? "text-amber-700" : "text-rose-700"}`}>
                    {c.roas}×
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function UTMView({ utm }) {
  return (
    <div className="space-y-8">
      <Table
        title="By traffic source"
        headers={["Source", "Visits", "Orders", "CVR", "Revenue"]}
        rows={utm.by_source.map((r) => [r.source, r.visits, r.orders || 0, `${r.cvr}%`, fmt(r.revenue || 0)])}
      />
      <Table
        title="By campaign"
        headers={["Campaign", "Source", "Visits", "Orders", "CVR", "Revenue"]}
        rows={utm.by_campaign.map((r) => [r.campaign, r.source, r.visits, r.orders || 0, `${r.cvr}%`, fmt(r.revenue || 0)])}
      />
    </div>
  );
}

function Table({ title, headers, rows }) {
  return (
    <div className="rounded-3xl bg-white border border-blush-100 overflow-hidden">
      <div className="px-6 py-4 border-b border-blush-100">
        <h3 className="font-heading text-xl text-ink-900">{title}</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm font-body">
          <thead>
            <tr className="bg-blush-50/40 text-left text-[11px] uppercase tracking-[0.18em] text-ink-500">
              {headers.map((h) => <th key={h} className="px-6 py-3">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={headers.length} className="px-6 py-10 text-center text-ink-500">No data.</td></tr>
            ) : rows.map((row, i) => (
              <tr key={i} className="border-t border-blush-100">
                {row.map((c, j) => <td key={j} className="px-6 py-3 text-ink-900">{c}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
